/*
 * trial-clicker-name.js - the name box and key colours on the Custom Fidget Clicker page.
 *
 * This is a BLOCK inside Horizon's ordinary product page, not a replacement for it. Horizon's
 * variant picker owns Style, Base colour and Keys; this owns the name and the per-key colours,
 * which cannot be product options (ten keys across four colourways is over a million combinations,
 * and Shopify allows three options).
 *
 * A plain custom element rather than Horizon's Component base class, because this ships as a
 * classic <script> from the block and must not depend on the theme's import map.
 *
 * THE ONE THING THAT BITES. Horizon's variant picker does NOT re-render custom blocks. Choosing a
 * different key count updates the price, the buy button and the form's hidden variant id, and
 * leaves this element exactly as the server first drew it. So the key count is read from the
 * picker's own checked inputs on every change - see #readPicker - and never from the data-
 * attribute alone, which is only right on first paint. The name is also stashed in sessionStorage,
 * so it survives the cases where the page IS rebuilt.
 */
(() => {
  const debounce = (fn, ms) => { let h; return (...a) => { clearTimeout(h); h = setTimeout(() => fn(...a), ms); }; };

  const ICONS = ['PAW', 'CAT'];
  /**
   * The two icon keys, drawn rather than spelled.
   *
   * Rishab, 21 Sep: "when I click on the Add Paw key ... it writes 'paw' instead of showing an
   * actual paw". The input still carries the literal tokens [PAW] and [CAT], because that string is
   * what the packer reads off the order slip and what products/clicker-stock.mjs parses - only the
   * picture of the key changes.
   *
   * These are simple shapes of our own, not the artwork from the licensed model set, and they are a
   * stand-in for a photograph of the real printed key the same way the letter tiles are.
   */
  const ICON_SVG = {
    PAW:
      '<svg viewBox="0 0 100 100" aria-hidden="true">' +
      '<ellipse cx="28" cy="34" rx="11" ry="14"/><ellipse cx="50" cy="26" rx="11" ry="14"/>' +
      '<ellipse cx="72" cy="34" rx="11" ry="14"/><ellipse cx="86" cy="56" rx="9" ry="11"/>' +
      '<path d="M50 48c16 0 28 11 28 23 0 9-8 14-18 14-5 0-7-2-10-2s-5 2-10 2c-10 0-18-5-18-14 0-12 12-23 28-23z"/>' +
      '</svg>',
    CAT:
      '<svg viewBox="0 0 100 100" aria-hidden="true">' +
      '<path d="M22 46 16 16l26 13z"/><path d="M78 46 84 16 58 29z"/>' +
      '<ellipse cx="50" cy="56" rx="32" ry="27"/>' +
      '</svg>',
  };
  const WAYS = ['BW', 'BG', 'GB', 'GW'];

  const esc = (v) =>
    String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /**
   * Turns what is in the box into the keys it means.
   *   - upper case; A-Z and 0-9 are one key each
   *   - [PAW] and [CAT] are one key each, and backspacing into one removes the whole token
   *   - anything past `max` keys is dropped
   * `reason` says WHY something was dropped, because running out of room and typing a character we
   * do not make need different sentences.
   */
  function readName(raw, max) {
    const src = String(raw ?? '').toUpperCase();
    const keys = [];
    let text = '';
    let reason = null;
    let at = 0;

    while (at < src.length) {
      if (keys.length >= max) { reason = reason ?? 'full'; break; }
      const ch = src[at];

      if (ch === '[') {
        const close = src.indexOf(']', at);
        if (close === -1) { reason = 'bad'; break; }
        const inner = src.slice(at + 1, close);
        if (ICONS.includes(inner)) { keys.push(inner); text += `[${inner}]`; } else { reason = 'bad'; }
        at = close + 1;
        continue;
      }

      if (/[A-Z0-9]/.test(ch)) { keys.push(ch); text += ch; } else { reason = 'bad'; }
      at += 1;
    }

    return { text, keys, reason };
  }

  class TrialClickerName extends HTMLElement {
    /*
     * Horizon MORPHS the product details on a variant change rather than replacing them: the element
     * survives, its data- attributes are updated in place, and connectedCallback never fires again.
     * So reading the key count once on connect left it stuck at whatever the page first rendered -
     * pick 4 keys and the box still only accepted one character. Watching the attribute covers both
     * cases: a morph updates it here, and a full replacement still runs connectedCallback.
     */
    static observedAttributes = ['data-key-count', 'data-style', 'data-base'];

    /**
     * How the option names are found in the picker. Horizon names each radio group
     * "<Option name>-<hash>__<block id>-<product id>", so the prefix is the option's own name.
     */
    static PICKER = { count: 'Keys-', style: 'Style-', base: 'Base colour-' };

    #keyColours = [];
    #way = 'BW';
    #mixing = false;
    #colours = {};
    #max = 0;
    #ready = false;
    #driving = false;
    #typed = 0;
    #commit = null;

    attributeChangedCallback(name, before, after) {
      if (before === after || !this.#ready) return;
      if (name === 'data-key-count') this.#max = Number(after || 0);
      this.#render();
    }

    /**
     * Reads the three options straight out of Horizon's variant picker.
     *
     * WHY, AND THIS IS THE WHOLE BUG RISHAB HIT. Horizon's variant picker does NOT re-render custom
     * blocks. Picking 4 keys updates the price, the buy button and the form's hidden variant id -
     * and leaves this element exactly as the server first drew it. So the data- attributes are only
     * ever right on first paint, watching them is not enough, and the name box stayed stuck at one
     * character however many keys were chosen. The picker's own checked inputs are the truth.
     */
    #readPicker() {
      const read = (prefix) => {
        const checked = document.querySelector(`input[type="radio"][name^="${prefix}"]:checked`);
        if (checked) return checked.value.trim();
        const select = document.querySelector(`select[name^="${prefix}"]`);
        return select ? String(select.value).trim() : null;
      };
      const P = TrialClickerName.PICKER;
      return { count: read(P.count), style: read(P.style), base: read(P.base) };
    }

    /** Pulls the picker's current values in. Returns true if anything moved. */
    #pull() {
      const { count, style, base } = this.#readPicker();
      const max = Number(count || this.dataset.keyCount || 0);
      let moved = false;
      if (max > 0 && max !== this.#max) { this.#max = max; moved = true; }
      if (style && style !== this.dataset.style) { this.dataset.style = style; moved = true; }
      if (base && base !== this.dataset.base) { this.dataset.base = base; moved = true; }
      return moved;
    }

    /**
     * Hides Horizon's "Keys" group and drives it from the typed name instead.
     *
     * WHY. Rishab, 21 Sep: "it's not the correct funnel ... so much going on". Asking someone to
     * pick the number of keys AND then type a name of exactly that length is the same fact entered
     * twice, and it cost two rows of ten buttons plus a whole error message about the two not
     * matching. The number of keys IS the length of the name. So the group stays in the DOM -
     * Horizon's own price, variant id and gallery updates all hang off those radios - and this
     * clicks the right one when the name changes.
     */
    #keysGroup() {
      const any = document.querySelector(`input[type="radio"][name^="${TrialClickerName.PICKER.count}"]`);
      if (!any) return null;
      // NOT closest('[class*="variant-option"]'): Horizon wraps every single radio in a
      // <label class="variant-option__button-label">, so that matches the label around ONE key
      // count rather than the group of ten. Climb to the fieldset, and fall back to the ancestor
      // that actually holds all of them.
      const fieldset = any.closest('fieldset');
      if (fieldset) return fieldset;
      let node = any.parentElement;
      while (node && node.querySelectorAll(`input[name^="${TrialClickerName.PICKER.count}"]`).length < 2) {
        node = node.parentElement;
      }
      return node;
    }

    #hideKeysGroup() {
      const group = this.#keysGroup();
      if (group && !group.hasAttribute('data-tcn-hidden')) {
        group.setAttribute('data-tcn-hidden', '');
        // Hidden from sight and from the tab order, but still submitted and still driving Horizon.
        group.style.display = 'none';
      }
    }

    /** Clicks the Keys radio matching a length. Guarded so it cannot re-enter its own listener. */
    #setKeys(n) {
      if (!n || n === this.#max) return;
      const radio = [...document.querySelectorAll(`input[type="radio"][name^="${TrialClickerName.PICKER.count}"]`)]
        .find((i) => i.value.trim() === String(n));
      if (!radio || radio.checked) return;
      this.#driving = true;
      radio.click();
      // Horizon's update is async; the flag is cleared after its own change handler has run, so
      // our listener ignores the echo of our own click.
      //
      // NEVER on a frame alone. requestAnimationFrame does not run AT ALL in a background tab, so
      // one rAF here left this flag stuck true and the block permanently deaf to the picker: the
      // price and the variant kept moving while _Style, _Base colour and _Key count silently
      // froze at whatever they were on first paint. On a phone, where switching apps mid-build is
      // normal, that is an order for the wrong object. setTimeout keeps running when hidden, so it
      // is the floor; whichever lands first wins and the second is harmless.
      const release = () => { this.#driving = false; };
      requestAnimationFrame(release);
      setTimeout(release, 250);
    }

    #onPickerChange = (event) => {
      if (this.#driving) return;
      const t = event.target;
      if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;
      const name = t.name || '';
      if (!Object.values(TrialClickerName.PICKER).some((p) => name.startsWith(p))) return;
      // Horizon swaps the price and the hidden variant id asynchronously. Read now for the common
      // case and once more on the next frame in case the picker itself was re-rendered under us.
      this.#pull();
      this.#render();
      requestAnimationFrame(() => { this.#pull(); this.#render(); });
    };

    connectedCallback() {
      this.#max = Number(this.dataset.keyCount || 0);
      try {
        this.#colours = JSON.parse(this.querySelector('[ref="colours"]')?.textContent || '{}');
      } catch { this.#colours = {}; }

      this.refs = {};
      for (const el of this.querySelectorAll('[ref]')) this.refs[el.getAttribute('ref')] = el;

      this.#paintWays();

      // Horizon rebuilds this element on every variant change, so anything typed is restored here.
      const saved = this.#load();
      if (saved?.name && this.refs.input) this.refs.input.value = saved.name;
      if (saved?.way && WAYS.includes(saved.way)) this.#way = saved.way;
      if (Array.isArray(saved?.keyColours)) this.#keyColours = saved.keyColours.slice();
      if (saved?.mixing) {
        this.#mixing = true;
        if (this.refs.mix) this.refs.mix.checked = true;
      }
      const chosen = this.querySelector(`input[name="tcn-way-${this.dataset.product}"][value="${this.#way}"]`);
      if (chosen) chosen.checked = true;

      this.addEventListener('input', this.#onInput);
      this.addEventListener('change', this.#onChange);
      this.addEventListener('click', this.#onClick);

      // Delegated on the document: the picker's inputs are replaced whenever Horizon re-renders it,
      // so a listener bound to them directly would go deaf after the first change.
      document.addEventListener('change', this.#onPickerChange);

      this.#hideKeysGroup();

      // Committing on every keystroke would fire a variant update per character. 350 ms after the
      // last one is imperceptible and costs one request instead of six.
      this.#commit = debounce(() => this.#setKeys(this.#typed), 350);

      this.#ready = true;
      this.#pull();
      this.#render();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.#onPickerChange);
      this.removeEventListener('input', this.#onInput);
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('click', this.#onClick);
    }

    get #storeKey() { return `tcn:${this.dataset.product}`; }

    #save(state) {
      try { sessionStorage.setItem(this.#storeKey, JSON.stringify(state)); } catch { /* private window */ }
    }

    #load() {
      try { return JSON.parse(sessionStorage.getItem(this.#storeKey) || 'null'); } catch { return null; }
    }

    #paintWays() {
      const host = this.refs.ways;
      if (!host) return;
      host.innerHTML = WAYS.map((code) => {
        const c = this.#colours[code] ?? {};
        return `<label class="tcn__way" title="${esc(c.label ?? code)}">
          <input type="radio" name="tcn-way-${esc(this.dataset.product)}" value="${code}" aria-label="${esc(c.label ?? code)}">
          <span class="tcn__dot" style="background:${esc(c.body ?? '#ccc')};color:${esc(c.letter ?? '#000')}" aria-hidden="true">A</span>
          <span class="tcn__way-name">${esc(c.label ?? code)}</span>
        </label>`;
      }).join('');
    }

    #onInput = (event) => {
      if (event.target === this.refs.input) this.#render();
    };

    #onChange = (event) => {
      const t = event.target;
      if (t === this.refs.mix) { this.#mixing = t.checked; this.#render(); return; }
      if (t instanceof HTMLInputElement && t.type === 'radio' && WAYS.includes(t.value)) {
        this.#way = t.value;
        // Changing the one colour clears any per-key overrides, so what is on screen is always what
        // was last asked for rather than a half-remembered mixture.
        this.#keyColours = [];
        this.#render();
      }
    };

    #onClick = (event) => {
      const icon = event.target.closest('[data-icon]');
      if (icon) { this.#insert(`[${icon.dataset.icon}]`); return; }

      // The whole tile is the target, not just the little code chip under it. The instruction says
      // "tap any key below, or the code under it", and a 2.5rem key is a far easier tap on a phone
      // than a 10px chip.
      const tile = event.target.closest('[data-swap]');
      if (tile && this.#mixing) {
        const i = Number(tile.dataset.swap);
        const now = this.#keyColours[i] ?? this.#way;
        this.#keyColours[i] = WAYS[(WAYS.indexOf(now) + 1) % WAYS.length];
        this.#render();
      }
    };

    /** Puts an icon token in at the caret rather than at the end. */
    #insert(token) {
      const input = this.refs.input;
      if (!input) return;
      const at = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? at;
      input.value = `${input.value.slice(0, at)}${token}${input.value.slice(end)}`;
      const caret = at + token.length;
      input.focus();
      input.setSelectionRange(caret, caret);
      this.#render();
    }

    /**
     * Re-resolves the refs and repaints the colour picker if a morph has moved the ground under us.
     * Horizon replaces child nodes when it updates the product details, which can leave the cached
     * refs pointing at detached elements and wipe out anything this element drew itself.
     */
    #sync() {
      if (!this.refs?.input?.isConnected) {
        this.refs = {};
        for (const el of this.querySelectorAll('[ref]')) this.refs[el.getAttribute('ref')] = el;
      }
      if (this.refs.ways && this.refs.ways.children.length === 0) {
        this.#paintWays();
        const chosen = this.querySelector(`input[name="tcn-way-${this.dataset.product}"][value="${this.#way}"]`);
        if (chosen) chosen.checked = true;
      }
    }

    #render() {
      this.#sync();
      this.#hideKeysGroup();
      const input = this.refs.input;
      if (!input) return;

      // No ceiling from the variant any more - the name decides the length, up to the ten we make.
      const read = readName(input.value, 10);
      if (input.value !== read.text) {
        const caret = input.selectionStart ?? read.text.length;
        input.value = read.text;
        const to = Math.min(caret, read.text.length);
        input.setSelectionRange(to, to);
      }

      const keys = read.keys;
      const colourOf = (i) => (this.#mixing ? this.#keyColours[i] ?? this.#way : this.#way);

      this.#typed = keys.length;
      if (this.refs.counter) this.refs.counter.textContent = `${keys.length}/10`;
      if (this.#ready && this.#commit) this.#commit();

      // Tiles
      if (this.refs.tiles) {
        this.refs.tiles.innerHTML = keys.length === 0
          ? `<span class="tcn__empty">${esc(this.dataset.msgShort ? '' : '')}</span>`
          : keys.map((glyph, i) => {
              const c = this.#colours[colourOf(i)] ?? {};
              const icon = ICONS.includes(glyph);
              const way = colourOf(i);
              const named = this.#colours[way]?.label ?? way;
              const cap = `<span class="tcn__cap${icon ? ' tcn__cap--icon' : ''}" style="background:${esc(c.body ?? '#ccc')};color:${esc(c.letter ?? '#000')}">${icon ? ICON_SVG[glyph] : esc(glyph)}</span>`;
              return this.#mixing
                ? `<button type="button" class="tcn__tile" data-swap="${i}" aria-label="Key ${i + 1}, ${esc(glyph)}, ${esc(named)}. Press to change its colour.">
                     ${cap}<span class="tcn__swap" aria-hidden="true">${esc(way)}</span>
                   </button>`
                : `<span class="tcn__tile" role="img" aria-label="Key ${i + 1}, ${esc(glyph)}, ${esc(named)}">
                     ${cap}<span class="tcn__swap" aria-hidden="true">${esc(way)}</span>
                   </span>`;
            }).join('');
      }

      if (this.refs.chosen) {
        const name = this.#colours[this.#way]?.label ?? this.#way;
        if (this.refs.chosen.textContent !== name) this.refs.chosen.textContent = name;
      }
      this.toggleAttribute('data-mixing', this.#mixing);
      if (this.refs.howto) this.refs.howto.hidden = !this.#mixing;

      // The message. Wrong length is not the same event as a character we do not make.
      const right = keys.length > 0;
      const message = read.reason === 'bad' ? this.dataset.msgBad ?? '' : '';
      if (this.refs.error) {
        this.refs.error.hidden = message === '';
        if (this.refs.error.textContent !== message) this.refs.error.textContent = message;
      }
      input.setAttribute('aria-invalid', right ? 'false' : 'true');

      // What goes on the order. Disabled until the name is the right length, so a mismatched order
      // cannot be added at all - the browser's own `required` blocks an empty one before this runs.
      const ok = right;
      const set = (ref, value) => {
        const el = this.refs[ref];
        if (!el) return;
        el.value = value;
        el.disabled = !ok;
      };
      // What travels with the order is read from the picker HERE, at the moment it is written -
      // not from this.dataset. The dataset is only ever as fresh as the last #pull(), and #pull()
      // depends on an event listener firing. The order's contents are the one thing on this page
      // that must never be stale: get them wrong and the wrong object gets built and posted, on an
      // item that is made to order and not refundable. The dataset stays as the fallback for the
      // first paint, before anything has been touched.
      const picked = this.#readPicker();
      set('propKeys', keys.map((g, i) => `${g}:${colourOf(i)}`).join(','));
      set('propStyle', picked.style ?? this.dataset.style ?? '');
      set('propBase', picked.base ?? this.dataset.base ?? '');
      set('propCount', String(keys.length));

      this.#save({ name: input.value, way: this.#way, mixing: this.#mixing, keyColours: this.#keyColours });
    }
  }

  if (!customElements.get('trial-clicker-name')) {
    customElements.define('trial-clicker-name', TrialClickerName);
  }
})();
